import { useCallback, useMemo } from 'react';
import { useLocalStorage } from 'react-use';

import { LOCAL_STORAGE_KEYS } from '@/constants/localStorageKeys';
import { localStorageStringArraySerializer } from '@/utils/localStorageSerializers';

interface UseBalanceSheetSuggestionsBannerDismissedInput {
  householdId: string;
  currentSuggestionIds: string[];
}

interface UseBalanceSheetSuggestionsBannerDismissedResult {
  isDismissed: boolean;
  dismiss: () => void;
}

/**
 * Tracks dismissed state of the balance sheet unacknowledged suggestions
 * banner for a given household.
 *
 * Dismissal is scoped per household and is remembered only for the current
 * set of suggestion IDs. If any suggestion ID appears that wasn't present
 * when the banner was dismissed, the banner is shown again.
 */
export function useBalanceSheetSuggestionsBannerDismissed({
  householdId,
  currentSuggestionIds,
}: UseBalanceSheetSuggestionsBannerDismissedInput): UseBalanceSheetSuggestionsBannerDismissedResult {
  const [dismissedIds, setDismissedIds] = useLocalStorage<string[]>(
    `${LOCAL_STORAGE_KEYS.BALANCE_SHEET_SUGGESTIONS_BANNER_DISMISSED}-${householdId}`,
    [],
    localStorageStringArraySerializer
  );

  const isDismissed = useMemo(() => {
    if (currentSuggestionIds.length === 0) return false;
    const dismissedSet = new Set(dismissedIds ?? []);
    return currentSuggestionIds.every((id) => dismissedSet.has(id));
  }, [currentSuggestionIds, dismissedIds]);

  const dismiss = useCallback(() => {
    setDismissedIds([...currentSuggestionIds].sort());
  }, [currentSuggestionIds, setDismissedIds]);

  return { isDismissed, dismiss };
}
